import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, ClipboardCheck, AlertTriangle, User } from 'lucide-react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { isURL } from 'validator';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import GoalsConstants from '../../../../constants/goals.constants';
import ReviewConstants from '../../../../constants/review.constants';
import ApiService from '../../../../services/api.service';
import {
  checkIsEverythingApproved,
  refreshSkillStatus,
  checkIsEverythingApprovedWithPercentage,
  checkIsEverythingFinished,
  getAllDetails,
} from '../../helpers/reviews.helpers';
import DeleteReviewModal from './components/DeleteReviewModal';
import ReviewActionsPanel from './components/ReviewActionsPanel';
import ReviewRecordModal from './components/ReviewRecordModal';
import SkillsReviewList from './components/SkillsReviewList';

const defaultReviewLinkState = {
  isOpen: false,
  link: '',
  isLinkError: false,
  callbackName: '',
};

const defaultReasonState = {
  isOpen: false,
  reason: '',
  callbackName: '',
};

function SpecificReviewV3() {
  const history = useHistory();
  const location = useLocation();
  const { department, reviewId } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const userEmail = searchParams.get('user');

  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState(null);
  const [skills, setSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAllApproved, setIsAllApproved] = useState(false);
  const [isAllFinished, setIsAllFinished] = useState(false);
  const [isAllApprovedWithPercentage, setIsAllApprovedWithPercentage] = useState(false);
  const [isLastLevel, setIsLastLevel] = useState(false);
  const [reviewLinkModal, setReviewLinkModal] = useState(defaultReviewLinkState);
  const [reasonValue, setReasonValue] = useState(defaultReasonState);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const getNextGoals = useCallback(
    async (isApproved, memberId) => {
      if (isApproved) return;

      const [levels, { goal }] = await Promise.all([
        ApiService.head.getDepartmentLevels(department),
        ApiService.head.getUser(memberId),
      ]);

      const goalIndex = levels.findIndex((v) => v._id === goal);
      const nextGoal = levels.splice(goalIndex + 1);

      if (!nextGoal.length) {
        setIsLastLevel(true);
      }

      setUserLevel(nextGoal);
    },
    [department],
  );

  const getSkillDetails = (skillsDetails) => {
    return skillsDetails.map((skill) => {
      return skill.details.map((detail) => ({
        name: detail.name,
        successCriteria: detail.successCriteria,
        materialName: detail.materialName,
        link: detail.link,
        status: detail.status,
      }));
    });
  };

  const getReviewGoals = useCallback(
    async (argUserId) => {
      const reviewSkills = await ApiService.head.getUserGoals(department, argUserId || userId);
      const allDetails = getAllDetails(reviewSkills);

      setSkills(reviewSkills);
      setIsLoading(false);

      const skillDetails = getSkillDetails(reviewSkills);
      const isApproved = checkIsEverythingApproved(skillDetails);
      const isApprovedWithPercentage = checkIsEverythingApprovedWithPercentage(skillDetails);
      const isFinished = checkIsEverythingFinished(allDetails);

      setIsAllApproved(isApproved);
      setIsAllApprovedWithPercentage(isApprovedWithPercentage);
      setIsAllFinished(isFinished);

      return getNextGoals(isApproved, argUserId);
    },
    [department, getNextGoals, userId],
  );

  const handleOnApprove = async (goalId, itemId, recommendation) => {
    await ApiService.head.acceptUserGoal({
      department,
      userId,
      goalId,
      itemId,
      recommendation,
    });

    const updatedSkills = refreshSkillStatus({
      skills,
      userId,
      goalId,
      itemId,
      recommendation,
      status: GoalsConstants.GOAL_STATUSES.APPROVED,
    });
    const allDetails = getAllDetails(updatedSkills);
    const skillDetails = getSkillDetails(updatedSkills);

    const isApproved = checkIsEverythingApproved(skillDetails);
    const isApprovedWithPercentage = checkIsEverythingApprovedWithPercentage(skillDetails);
    const isFinished = checkIsEverythingFinished(allDetails);

    setSkills(updatedSkills);
    setIsAllApproved(isApproved);
    setIsAllApprovedWithPercentage(isApprovedWithPercentage);
    setIsAllFinished(isFinished);

    return getNextGoals(isApproved, userId);
  };

  const handleOnDecline = async ({ goalId, itemId, comment }) => {
    await ApiService.head.declineUserGoal({
      department,
      userId,
      goalId,
      itemId,
      comment,
    });

    const updatedSkills = refreshSkillStatus({
      skills,
      userId,
      goalId,
      itemId,
      status: GoalsConstants.GOAL_STATUSES.DECLINED,
      comment,
    });
    const allDetails = getAllDetails(updatedSkills);
    const updatedSkillDetails = getSkillDetails(updatedSkills);

    const isApproved = checkIsEverythingApproved(updatedSkillDetails);
    const isApprovedWithPercentage = checkIsEverythingApprovedWithPercentage(updatedSkillDetails);
    const isFinished = checkIsEverythingFinished(allDetails);

    setSkills(updatedSkills);
    setIsAllApproved(isApproved);
    setIsAllApprovedWithPercentage(isApprovedWithPercentage);
    setIsAllFinished(isFinished);

    return getNextGoals(isApproved, userId);
  };

  const getApprovedAndDeclinedDetails = () => {
    const uniqueDetails = {};

    skills.forEach((skill) => {
      skill.details.forEach((detail) => {
        const { name, status, comment, recommendation, itemId } = detail;

        if (!uniqueDetails[name]) {
          uniqueDetails[name] = {
            itemId: [],
            name,
            status,
            comment: [],
            recommendation: [],
          };
        }

        uniqueDetails[name].itemId.push(itemId);
        uniqueDetails[name].comment.push(comment);
        uniqueDetails[name].recommendation.push(recommendation);
      });
    });

    const confirmed = [];
    const declined = [];

    Object.values(uniqueDetails).forEach((detail) => {
      const { name, status, comment, recommendation, itemId } = detail;
      if (status === GoalsConstants.GOAL_STATUSES.APPROVED) {
        confirmed.push({ itemId: itemId[0], name, status, recommendation: recommendation[0] });
      } else if (status === GoalsConstants.GOAL_STATUSES.DECLINED) {
        declined.push({ itemId: itemId[0], name, status, comment: comment[0] });
      }
    });

    return { confirmed, declined };
  };

  const handleLinkInput = ({ target }) => {
    const value = target.value.trim();
    const isValidUrl = isURL(value, {
      protocols: ['http', 'https'],
      require_protocol: true,
    });

    setReviewLinkModal({
      ...reviewLinkModal,
      link: value,
      isLinkError: value.length === 0 ? false : !isValidUrl,
    });
  };

  const handleDeleteReasonInput = ({ target }) => {
    setReasonValue({ ...reasonValue, reason: target.value });
  };

  const handleOnClearModal = (callbackName) => {
    if (callbackName !== 'handleOnDelete') {
      setReviewLinkModal({ ...reviewLinkModal, link: '', isLinkError: false });
    } else {
      setReasonValue({ ...reasonValue, reason: '' });
    }
  };

  const onOpenModal = (callbackName) => {
    if (callbackName !== 'handleOnDelete') {
      setReviewLinkModal({ ...reviewLinkModal, callbackName, isOpen: true });
    } else {
      setReasonValue({ ...reasonValue, callbackName, isOpen: true });
    }
  };

  const handleOnCloseModal = () => {
    setReviewLinkModal(defaultReviewLinkState);
    setReasonValue(defaultReasonState);
  };

  const handleOnAddCurrentRecordingLink = () => {
    setReviewLinkModal({
      ...reviewLinkModal,
      callbackName: 'handleOnAddCurrentRecordingLink',
      isOpen: true,
    });
  };

  const handleOnAddCurrentRecordingLinkSave = async (link) => {
    try {
      setIsSaveLoading(true);
      await ApiService.head.addCurrentRecordingLink({
        department,
        reviewId,
        body: { reviewRecordLink: link },
      });
      setMessage('Recording link added successfully');
      setReviewLinkModal(defaultReviewLinkState);
    } catch (err) {
      setError('Failed to add recording link');
    } finally {
      setIsSaveLoading(false);
    }
  };

  const handleOnArchive = async () => {
    try {
      const { confirmed, declined } = getApprovedAndDeclinedDetails();

      await Promise.all([
        ApiService.head.archiveReview(department, reviewId, {
          confirmed: confirmed.length,
          declined: declined.length,
          reviewRecordLinks: reviewLinkModal.link ? [reviewLinkModal.link] : [],
        }),
        ApiService.head.sendTaskStatuses(department, reviewId, [...confirmed, ...declined]),
      ]);

      await ApiService.head.sendReviewStatistic(department, userId);
    } finally {
      handleOnCloseModal();
      setUserId(null);
      history.goBack();
    }
  };

  const handleOnDelete = async () => {
    try {
      setIsSaveLoading(true);

      await ApiService.head.deleteUserReview({
        department,
        reviewId,
        reason: reasonValue.reason,
      });

      handleOnCloseModal();
      setUserId(null);
      history.goBack();
    } catch (err) {
      console.error('Error deleting review:', err);
      setError(err.response?.data?.message || 'Failed to delete review');
      setIsSaveLoading(false);
    }
  };

  const handleOnSetNextLevelClick = async () => {
    try {
      const { confirmed, declined } = getApprovedAndDeclinedDetails();

      await Promise.all([
        ApiService.head.confirmReview(department, reviewId, {
          confirmed: confirmed.length,
          declined: declined.length,
          reviewRecordLinks: reviewLinkModal.link ? [reviewLinkModal.link] : [],
        }),
        ApiService.head.setUserNextGoal(userId),
        ApiService.head.sendTaskStatuses(department, reviewId, [...confirmed, ...declined]),
      ]);

      await ApiService.head.sendReviewStatistic(department, userId);
    } finally {
      handleOnCloseModal();
      setUserId(null);
      history.goBack();
    }
  };

  const handleOnSaveModal = async () => {
    // handleOnDelete manages its own loading state and modal closing
    if (reasonValue.callbackName === 'handleOnDelete') {
      await handleOnDelete();
      return;
    }

    setIsSaveLoading(true);
    try {
      if (reviewLinkModal.callbackName === 'handleOnSetNextLevelClick') {
        await handleOnSetNextLevelClick();
      }
      if (reviewLinkModal.callbackName === 'handleOnArchive') {
        await handleOnArchive();
      }
      if (reviewLinkModal.callbackName === 'handleOnAddCurrentRecordingLink') {
        await handleOnAddCurrentRecordingLinkSave(reviewLinkModal.link);
      }
    } finally {
      setIsSaveLoading(false);
      handleOnCloseModal();
    }
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        const userReview = await ApiService.head.getDepartmentMemberReview(department, reviewId);

        if (!userReview || userReview.status !== ReviewConstants.REVIEW_STATUSES.PROCESSED) {
          if (isMounted) {
            setError(ReviewConstants.ERROR_MESSAGES.REVIEW_NOT_FOUND);
          }
          return;
        }

        setUserId(userReview.userId);
        await getReviewGoals(userReview.userId);
      } catch (err) {
        if (isMounted) {
          setError('Failed to load review data');
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [department, getReviewGoals, reviewId]);

  const isSaveDisabled = reviewLinkModal.isLinkError || reviewLinkModal.link.length === 0;
  const isReasonSaveDisabled = reasonValue.reason.length === 0;

  const approvedCount = skills.reduce((acc, skill) => {
    return (
      acc + skill.details.filter((d) => d.status === GoalsConstants.GOAL_STATUSES.APPROVED).length
    );
  }, 0);

  const declinedCount = skills.reduce((acc, skill) => {
    return (
      acc + skill.details.filter((d) => d.status === GoalsConstants.GOAL_STATUSES.DECLINED).length
    );
  }, 0);

  const totalDetails = skills.reduce((acc, skill) => acc + skill.details.length, 0);
  const pendingCount = totalDetails - approvedCount - declinedCount;

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => history.goBack()}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="p-2 rounded-lg bg-primary/10">
              <ClipboardCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Review Skills</h1>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <User className="h-3.5 w-3.5" />
                {userEmail || 'Loading...'}
              </div>
            </div>
          </div>
        </div>

        {/* Warning Alert */}
        <div
          className="flex items-center gap-3 p-4 rounded-lg border bg-amber-500/10"
          style={{ borderColor: 'hsl(var(--border))' }}
        >
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-foreground">
            <span className="font-medium">Important:</span> A review recording link is required to
            complete this review.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-amber-500">{pendingCount}</div>
              <div className="text-sm text-muted-foreground">Pending</div>
            </CardContent>
          </Card>
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-500">{approvedCount}</div>
              <div className="text-sm text-muted-foreground">Approved</div>
            </CardContent>
          </Card>
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-500">{declinedCount}</div>
              <div className="text-sm text-muted-foreground">Declined</div>
            </CardContent>
          </Card>
        </div>

        {/* Skills Review */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Skills to Review</CardTitle>
          </CardHeader>
          <CardContent>
            <SkillsReviewList
              skills={skills}
              isLoading={isLoading}
              onApprove={handleOnApprove}
              onDecline={handleOnDecline}
            />
          </CardContent>
        </Card>

        {/* Actions Panel */}
        <ReviewActionsPanel
          isAllApproved={isAllApproved}
          isAllFinished={isAllFinished}
          isAllApprovedWithPercentage={isAllApprovedWithPercentage}
          isLastLevel={isLastLevel}
          isDisabled={isLoading || !userId}
          onNextGoalClick={() => onOpenModal('handleOnSetNextLevelClick')}
          onArchive={() => onOpenModal('handleOnArchive')}
          onDelete={() => onOpenModal('handleOnDelete')}
          onAddRecordingLink={handleOnAddCurrentRecordingLink}
        />
      </div>

      {/* Review Record Modal */}
      <ReviewRecordModal
        isOpen={reviewLinkModal.isOpen}
        linkValue={reviewLinkModal.link}
        onLinkChange={handleLinkInput}
        onSave={handleOnSaveModal}
        onClose={handleOnCloseModal}
        onClear={() => handleOnClearModal()}
        isLinkError={reviewLinkModal.isLinkError}
        isSaveDisabled={isSaveDisabled}
        isSaveLoading={isSaveLoading}
      />

      {/* Delete Review Modal */}
      <DeleteReviewModal
        isOpen={reasonValue.isOpen}
        reasonValue={reasonValue.reason}
        onReasonChange={handleDeleteReasonInput}
        onSave={handleOnSaveModal}
        onClose={handleOnCloseModal}
        onClear={() => handleOnClearModal('handleOnDelete')}
        isSaveDisabled={isReasonSaveDisabled}
        isSaveLoading={isSaveLoading}
      />

      {/* Success Message */}
      {message && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          {message}
          <button
            type="button"
            onClick={() => setMessage('')}
            className="font-bold hover:opacity-80"
          >
            &times;
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          {error}
          <button type="button" onClick={() => setError('')} className="font-bold hover:opacity-80">
            &times;
          </button>
        </div>
      )}
    </ScrollArea>
  );
}

export default SpecificReviewV3;
